import {ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {AuthService} from '../../core/auth/auth.service';
import {Profil} from '../../shared/profil';
import {SaleChannel} from '../../shared/SaleChannel';
import {SaleChannelService} from '../../services/saleChannel.service';
import { AppLanguage, LanguageService } from '../../services/language.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { FiscalPrinterService } from '../../services/fiscal-printer.service';
import { AppTheme, ThemeService } from '../../services/theme.service';
import { ChangelogService } from '../../services/changelog.service';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { PermissionsService } from '../../core/permissions/permissions.service';
import { ChatSoundService } from '../../features/chat/services/chat-sound.service';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.sass', './../../app.component.sass'],
  encapsulation: ViewEncapsulation.None
})
export class MenuComponent implements OnInit, OnDestroy {
  token: string;
  user: Profil;
  logedUser: Profil;
  saleChannels: SaleChannel[] = [];
  currentLanguage = this.languageService.getCurrentLanguage();
  themes = this.themeService.themes;
  currentTheme = this.themeService.getCurrentTheme();
  hasUnseenChanges$ = this.changelogService.hasUnseen$;
  soundEnabled$ = this.chatSoundService.enabled$;

  /** Zakladka Statystyki - backend (/statistics/access): profil ma wezel sztuk, obrotow albo slabej rotacji. */
  statsAllowed = false;

  /** biezacy URL — do podswietlania sekcji rozwijanych (patrz isSectionActive) */
  private currentUrl = '';

  private langSub: Subscription;
  private routerSub: Subscription;

  constructor(
    private authService: AuthService,
    private saleChanneService: SaleChannelService,
    private languageService: LanguageService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private fiscalPrinterService: FiscalPrinterService,
    private themeService: ThemeService,
    private changelogService: ChangelogService,
    private router: Router,
    private http: HttpClient,
    private permissionsService: PermissionsService,
    private chatSoundService: ChatSoundService,
  ) {
    this.token = authService.getToken();
    this.user = JSON.parse(localStorage.getItem('user'));
    this.logedUser = this.authService.getLoggedUser();
  }

  async ngOnInit() {
    // Widocznosc zakladki Statystyki - nie moze wywrocic menu przy bledzie.
    this.http.get<{ allowed: boolean }>('/statistics/access').subscribe({
      next: (r) => { this.statsAllowed = !!r?.allowed; this.cdr.markForCheck(); },
      error: () => {},
    });

    // UWAGA: wszystko, co nie zalezy od sieci, ustawiamy PRZED pierwszym await.
    // Wczesniej `await this.getSaleChannels()` bylo pierwsze — gdy to zadanie
    // padalo (restart backendu, chwilowy brak sieci), WYJATEK przerywal cale
    // ngOnInit i po cichu zabijal wszystko ponizej: podswietlanie sekcji,
    // wskaznik dziennika zmian i reakcje na zmiane jezyka.

    // routerLinkActive dziala tylko na elementach z routerLink, a sekcje
    // rozwijane to <button matMenuTriggerFor> — dlatego stan aktywnosci
    // liczymy sami z biezacego URL-a.
    this.currentUrl = this.router.url;
    this.routerSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        this.currentUrl = e.urlAfterRedirects || e.url;
        this.cdr.detectChanges();
      });

    // Gdy tłumaczenia się załadują, wymuszamy detekcję zmian w kontrolowany sposób
    // – zapobiega NG0100 przy lazy load modułów
    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.cdr.detectChanges();
    });

    this.changelogService.init();

    // kanaly sprzedazy sa tylko do podmenu „Detal" — ich brak nie moze
    // przewrocic reszty menu
    try {
      await this.getSaleChannels();
    } catch {
      this.saleChannels = [];
    }
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
    this.routerSub?.unsubscribe();
  }

  /**
   * Czy ktoras z tras danej sekcji jest otwarta.
   * Sekcje rozwijane to <button matMenuTriggerFor>, a nie linki, wiec
   * routerLinkActive ich nie obsluzy — stad wlasne liczenie z URL-a.
   */
  isSectionActive(prefixes: string[]): boolean {
    const url = (this.currentUrl || '').split('?')[0];
    return prefixes.some((p) => url === '/' + p || url.startsWith('/' + p + '/'));
  }

  async getSaleChannels(){
    await this.saleChanneService.getSaleChannels().toPromise().then((res) => {
      // Kazdy kanal ma wlasny wezel uprawnien 'channel-access.<id>' (patrz
      // plan pkt 3) - user bez odczytu na danym kanale w ogole nie widzi
      // jego zakladki w podmenu Detal.
      this.saleChannels = res
        .filter(x => x.type=='detail')
        .filter(x => this.permissionsService.has('channel-access.' + x.id, 'read'));
    });
  }

  logout(){
    this.authService.logout();
  }

  changeLanguage(language: AppLanguage): void {
    this.languageService.setLanguage(language);
    this.currentLanguage = language;
  }

  changeTheme(theme: AppTheme): void {
    this.themeService.setTheme(theme);
    this.currentTheme = theme;
  }

  toggleSound(enabled: boolean): void {
    this.chatSoundService.setEnabled(enabled);
  }

  changePrinterIp(): void {
    this.fiscalPrinterService.changePrinterIp();
  }
}
